/**
 * ExportFinnegansModal — genera los archivos de importación de Finnegans a
 * partir del pedido OP adjunto a la obra.
 *
 * Muestra la previsualización: perfiles resueltos por código (con selector y
 * memoria para los ambiguos), los renglones agrupados (Accesorios Varios /
 * Vidrios Float PVC) y las telas en rollos (Ceci elige el tejido del maestro).
 * Descarga: Pedido_ObraN_R8.xls y Pedido_ObraN_Tela.xls.
 */

import { useState, useEffect, useMemo } from "react";
import {
  getProductosObras,
  getAliasGuardados,
  guardarAlias,
  getClientesLista,
  setClienteObra,
} from "../services/obrasFS";
import { archivosOPs } from "../services/estadosObra";
import {
  resolverItemsFinnegans,
  armarGrupo,
  descargarExcelPedido,
  COD_ACCESORIOS,
  COD_VIDRIOS,
} from "../services/exportFinnegans";

const rowStyle = { fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid #f1f5f9", color: "#334155" };
const tituloStyle = (color) => ({ fontSize: 13, fontWeight: 700, color, margin: "12px 0 4px" });

export default function ExportFinnegansModal({ obra, usuario, onClose, itemsOverride = null, sufijoArchivo = "", sufijoDesc = "" }) {
  const [estado, setEstado] = useState("cargando"); // cargando | listo | error
  const [error, setError] = useState("");
  const [renglones, setRenglones] = useState([]);
  const [productos, setProductos] = useState([]);
  const [buscarIdx, setBuscarIdx] = useState(null); // renglón en modo búsqueda
  const [qProd, setQProd] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [telas, setTelas] = useState(null); // { tipos, tejidos }
  const [selCodigos, setSelCodigos] = useState({}); // idx → codigo elegido
  const [telaSel, setTelaSel] = useState({});       // tipo → { codigo, cantidad }
  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(
    obra.clienteCodigo ? { codigo: obra.clienteCodigo, nombre: obra.cliente } : null
  );
  const [qCliente, setQCliente] = useState("");

  useEffect(() => {
    (async () => {
      try {
        // Los productos salen de TODOS los OP de la obra (producción en etapas)
        const archivosOp = archivosOPs(obra);
        if (archivosOp.length === 0 && !itemsOverride) throw new Error("La obra no tiene ningún pedido OP adjunto");

        const [productos, alias, clientesL] = await Promise.all([
          getProductosObras(),
          getAliasGuardados(),
          getClientesLista(),
        ]);
        if (productos.length === 0) {
          throw new Error('Primero importá el maestro de productos (botón "📦 Productos" del tablero)');
        }
        setClientes(clientesL);
        setProductos(productos);

        const { extraerItemsDeOPs, calcularTelas } = await import("../services/parseArchivosObra");
        // Modo normal: suma los ítems de todos los OP. Modo complementario:
        // recibe ya las diferencias a pedir (itemsOverride) sin leer archivos.
        const items = itemsOverride || await extraerItemsDeOPs(archivosOp);

        // Perfiles/refuerzos → pedido R8 · accesorios Karpen → pedido GUALE
        setRenglones(resolverItemsFinnegans(
          items.filter(i => ["finnegans", "perfiles", "karpen"].includes(i.categoria)),
          productos,
          alias
        ));

        const g = [];
        const acc = armarGrupo(items.filter(i => i.categoria === "plaza"), COD_ACCESORIOS, "Accesorios Varios");
        const vid = armarGrupo(items.filter(i => i.categoria === "vidrios"), COD_VIDRIOS, "Vidrios Float PVC");
        if (acc) g.push({ ...acc, destino: "guale" });
        if (vid) g.push({ ...vid, destino: "guale" });
        setGrupos(g);

        const itemsTelas = items.filter(i => i.categoria === "telas");
        if (itemsTelas.length > 0) {
          const calculo = calcularTelas(itemsTelas);
          const tejidos = productos.filter(p => p.activo && /tejido.*fibra/i.test(p.nombre));
          setTelas({ tipos: calculo.tipos, tejidos });
          const sel = {};
          for (const t of calculo.tipos) {
            sel[t.tipo] = { codigo: "", cantidad: t.rollos != null ? t.rollos.toFixed(2) : "" };
          }
          setTelaSel(sel);
        }

        setEstado("listo");
      } catch (e) {
        setError(e.message || String(e));
        setEstado("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientesFiltrados = useMemo(() => {
    const t = qCliente.trim().toLowerCase();
    if (t.length < 2) return [];
    return clientes.filter(c =>
      c.nombre.toLowerCase().includes(t) || String(c.codigo).includes(t)
    ).slice(0, 8);
  }, [clientes, qCliente]);

  function codigoDe(r, i) {
    return r.codigo || selCodigos[i] || null;
  }

  // Cada renglón conserva su índice global (para selCodigos) pero se muestra
  // y descarga según el pedido de destino
  const pares = renglones.map((r, i) => ({ r, i }));
  const paresR8 = pares.filter(({ r }) => r.categoria !== "karpen");
  const paresGuale = pares.filter(({ r }) => r.categoria === "karpen");
  const grupoVid = grupos.find(g => g.codigo === COD_VIDRIOS) || null;
  const grupoAcc = grupos.find(g => g.codigo === COD_ACCESORIOS) || null;

  const faltanR8 = paresR8.some(({ r, i }) => !codigoDe(r, i));
  const faltanGuale = paresGuale.some(({ r, i }) => !codigoDe(r, i));
  const faltanTelas = telas ? telas.tipos.some(t => !telaSel[t.tipo]?.codigo || !Number(telaSel[t.tipo]?.cantidad)) : false;

  const hayGuale = paresGuale.length > 0 || !!grupoAcc || !!grupoVid || !!telas;
  const puedeR8 = clienteSel && !faltanR8 && paresR8.length > 0;
  const puedeGuale = clienteSel && hayGuale && !faltanGuale && !faltanTelas;

  async function elegirCliente(c) {
    setClienteSel(c);
    setQCliente("");
    try { await setClienteObra(obra.id, c); } catch (e) { console.warn("[setClienteObra]", e); }
  }

  function elegirCodigo(idx, alias, codigo) {
    setSelCodigos(s => ({ ...s, [idx]: codigo }));
    setBuscarIdx(null);
    setQProd("");
    if (alias && codigo) guardarAlias(alias, codigo, usuario).catch(() => {});
  }

  const productosFiltrados = useMemo(() => {
    const t = qProd.trim().toLowerCase();
    if (t.length < 2) return [];
    return productos
      .filter(p => p.activo && (p.nombre.toLowerCase().includes(t) || String(p.codigo).toLowerCase().includes(t)))
      .slice(0, 6);
  }, [productos, qProd]);

  function nombreDeCodigo(codigo) {
    const p = productos.find(x => String(x.codigo) === String(codigo));
    return p ? p.nombre : "";
  }

  function descargarR8() {
    const filas = paresR8.map(({ r, i }) => ({
      codigo: codigoDe(r, i),
      cantidad: r.cant,
      precio: r.parcial || "",
      descripcionItem: r.detalle,
    }));
    descargarExcelPedido(`Pedido_Obra${obra.numero}${sufijoArchivo}_R8.xls`, filas, {
      clienteCodigo: clienteSel.codigo,
      descripcion: `R8 - OBRA ${obra.numero}${sufijoDesc}`,
    });
  }

  function descargarGuale() {
    const filas = paresGuale.map(({ r, i }) => ({
      codigo: codigoDe(r, i),
      cantidad: r.cant,
      precio: r.parcial || "",
      descripcionItem: r.detalle,
    }));
    if (grupoVid) {
      filas.push({ codigo: grupoVid.codigo, cantidad: grupoVid.cantidad, precio: grupoVid.precio, descripcionItem: `${grupoVid.nombre} según OP` });
    }
    if (grupoAcc) {
      filas.push({ codigo: grupoAcc.codigo, cantidad: grupoAcc.cantidad, precio: grupoAcc.precio, descripcionItem: `${grupoAcc.nombre} según OP` });
    }
    if (telas) {
      for (const t of telas.tipos) {
        const sel = telaSel[t.tipo];
        const cantidad = Number(sel.cantidad);
        filas.push({
          codigo: sel.codigo,
          cantidad,
          precio: cantidad > 0 && t.importe ? Math.round((t.importe / cantidad) * 100) / 100 : "",
          descripcionItem: `${t.tipo} — ${t.piezas} piezas según OP`,
        });
      }
    }
    descargarExcelPedido(`Pedido_Obra${obra.numero}${sufijoArchivo}_GUALE.xls`, filas, {
      clienteCodigo: clienteSel.codigo,
      descripcion: `GUALE - OBRA ${obra.numero}${sufijoDesc}`,
    });
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 285, padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, padding: 18, width: "100%",
          maxWidth: 640, maxHeight: "88vh", display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 17 }}>⬇️ Crear pedidos en Finnegans — obra {obra.numero}</h2>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>

        {estado === "cargando" && <div style={{ color: "#64748b", padding: 24, textAlign: "center" }}>Leyendo el pedido OP…</div>}
        {estado === "error" && (
          <div style={{ color: "#b91c1c", background: "#fee2e2", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
            {error}
          </div>
        )}

        {estado === "listo" && (
          <div style={{ overflowY: "auto", flex: 1 }}>
            {/* Cliente */}
            <div style={tituloStyle("#334155")}>Cliente (código del maestro)</div>
            {clienteSel ? (
              <div style={{ fontSize: 13 }}>
                ✓ <b>{clienteSel.nombre}</b> · código {clienteSel.codigo}
                <button className="btn btn--ghost" style={{ fontSize: 11.5, padding: "1px 8px", marginLeft: 6 }}
                  onClick={() => setClienteSel(null)}>
                  Cambiar
                </button>
              </div>
            ) : (
              <div>
                <input
                  value={qCliente}
                  onChange={e => setQCliente(e.target.value)}
                  placeholder="Buscar cliente por nombre o código…"
                  style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13, border: "1px solid #cbd5e1", borderRadius: 8 }}
                />
                {clientesFiltrados.map(c => (
                  <div key={c.codigo} onClick={() => elegirCliente(c)}
                    style={{ ...rowStyle, cursor: "pointer" }}>
                    {c.nombre} <span style={{ color: "#94a3b8" }}>· {c.codigo}</span>
                  </div>
                ))}
              </div>
            )}

            {(() => {
              const renderRenglon = ({ r, i }) => {
                const cod = codigoDe(r, i);
                return (
                  <div key={i} style={rowStyle}>
                    <b>{r.cant}×</b> {r.alias} — <span style={{ color: "#94a3b8" }}>{r.detalle.slice(0, 45)}</span>
                    <div style={{ marginTop: 2 }}>
                      {cod ? (
                        <span style={{ color: "#15803d", fontSize: 12 }}>
                          → {cod} · {(r.producto?.nombre || nombreDeCodigo(cod)).slice(0, 45)}
                          {r.origen === "memoria" ? " (recordado)" : ""}
                        </span>
                      ) : r.candidatos.length > 0 && buscarIdx !== i ? (
                        <select
                          defaultValue=""
                          onChange={e => e.target.value === "__buscar__" ? setBuscarIdx(i) : elegirCodigo(i, r.alias, e.target.value)}
                          style={{ fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1px solid #f59e0b", maxWidth: "100%" }}
                        >
                          <option value="" disabled>⚠️ Elegir producto del maestro…</option>
                          {r.candidatos.map(p => (
                            <option key={p.codigo} value={p.codigo}>{p.codigo} · {p.nombre.slice(0, 55)}</option>
                          ))}
                          <option value="__buscar__">🔍 Buscar otro en el maestro…</option>
                        </select>
                      ) : (
                        <div>
                          <input
                            autoFocus={buscarIdx === i}
                            value={buscarIdx === i ? qProd : ""}
                            onFocus={() => { setBuscarIdx(i); }}
                            onChange={e => { setBuscarIdx(i); setQProd(e.target.value); }}
                            placeholder="⚠️ Buscar producto en el maestro (nombre o código)…"
                            style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: "4px 8px", borderRadius: 6, border: "1px solid #f59e0b" }}
                          />
                          {buscarIdx === i && productosFiltrados.map(p => (
                            <div
                              key={p.codigo}
                              onClick={() => elegirCodigo(i, r.alias, String(p.codigo))}
                              style={{ fontSize: 12, padding: "4px 6px", cursor: "pointer", borderBottom: "1px solid #f8fafc", color: "#334155" }}
                            >
                              {p.codigo} · {p.nombre.slice(0, 55)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              };

              const destHeader = (icono, texto, color, bg) => (
                <div style={{
                  display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700,
                  color, background: bg, borderRadius: 8, padding: "6px 10px", margin: "10px 0 6px",
                }}>
                  {icono} {texto}
                </div>
              );

              return (
                <>
                  <div style={{ fontSize: 12, color: "#64748b", margin: "8px 0 2px" }}>
                    Así se reparten los productos del OP en los dos pedidos de Finnegans:
                  </div>

                  {/* ═══ Pedido R8 (perfiles/refuerzos) ═══ */}
                  {destHeader("📦", `PEDIDO R8${faltanR8 ? " — resolvé los marcados ⚠️" : ""}`, "#0369a1", "#e0f2fe")}
                  {paresR8.length > 0 && paresR8.map(renderRenglon)}
                  {paresR8.length === 0 && (
                    <div style={{ color: "#94a3b8", fontSize: 12, padding: "2px 8px" }}>Sin productos para R8.</div>
                  )}

                  {/* ═══ Pedido GUALE (karpen + acc. varios + vidrios + telas) ═══ */}
                  {destHeader("🏭", `PEDIDO GUALE${faltanGuale ? " — resolvé los marcados ⚠️" : ""}`, "#92400e", "#fef3c7")}
                  {paresGuale.length > 0 && paresGuale.map(renderRenglon)}
                  {grupoVid && (
                    <div style={rowStyle}>
                      <b>{grupoVid.cantidad}×</b> {grupoVid.nombre} <span style={{ color: "#94a3b8" }}>(agrupa {grupoVid.items?.length || ""} vidrios)</span>
                      <div style={{ color: "#15803d", fontSize: 12 }}>→ {grupoVid.codigo}</div>
                    </div>
                  )}
                  {grupoAcc && (
                    <div style={rowStyle}>
                      <b>{grupoAcc.cantidad}×</b> {grupoAcc.nombre} <span style={{ color: "#94a3b8" }}>(agrupa {grupoAcc.items?.length || ""} accesorios de plaza)</span>
                      <div style={{ color: "#15803d", fontSize: 12 }}>→ {grupoAcc.codigo}</div>
                    </div>
                  )}
                  {telas && telas.tipos.map(t => (
                    <div key={t.tipo} style={rowStyle}>
                      <b>{t.tipo}</b> ({t.piezas} piezas{t.rolloDesc ? `, rollo OP ${t.rolloDesc}` : ""})
                      <div style={{ display: "flex", gap: 6, marginTop: 3, flexWrap: "wrap", alignItems: "center" }}>
                        <select
                          value={telaSel[t.tipo]?.codigo || ""}
                          onChange={e => setTelaSel(s => ({ ...s, [t.tipo]: { ...s[t.tipo], codigo: e.target.value } }))}
                          style={{ fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1px solid #cbd5e1", flex: 1, minWidth: 200 }}
                        >
                          <option value="">Elegir tejido del maestro…</option>
                          {telas.tejidos.map(p => (
                            <option key={p.codigo} value={p.codigo}>{p.codigo} · {p.nombre.slice(0, 50)}</option>
                          ))}
                        </select>
                        <input
                          type="number" step="0.01" min="0"
                          value={telaSel[t.tipo]?.cantidad ?? ""}
                          onChange={e => setTelaSel(s => ({ ...s, [t.tipo]: { ...s[t.tipo], cantidad: e.target.value } }))}
                          style={{ width: 80, fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1px solid #cbd5e1" }}
                        />
                        <span style={{ fontSize: 11.5, color: "#94a3b8" }}>rollos</span>
                      </div>
                    </div>
                  ))}
                  {paresGuale.length === 0 && !grupoAcc && !grupoVid && !telas && (
                    <div style={{ color: "#94a3b8", fontSize: 12, padding: "2px 8px" }}>Sin productos para Gualeguay.</div>
                  )}
                </>
              );
            })()}

            <div style={{ fontSize: 11.5, color: "#94a3b8", margin: "10px 0" }}>
              Los precios salen del OP (columna PARCIAL; en los agrupados, importe ÷ cantidad).
              La fecha es la de hoy y el comprobante queda vacío (Finnegans lo asigna solo).
            </div>
          </div>
        )}

        {estado === "listo" && (
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <button className="btn" disabled={!puedeR8} onClick={descargarR8}>
              ⬇️ Pedido R8 (.xls)
            </button>
            {hayGuale && (
              <button className="btn btn--outline" disabled={!puedeGuale} onClick={descargarGuale}>
                ⬇️ Pedido GUALE (.xls)
              </button>
            )}
            {!clienteSel && <span style={{ fontSize: 12, color: "#b45309", alignSelf: "center" }}>Elegí el cliente para habilitar la descarga</span>}
          </div>
        )}
      </div>
    </div>
  );
}
